import React, { useCallback, useState } from "react";
import { useDropzone, FileRejection } from "react-dropzone";
import { PaperClipIcon } from "@heroicons/react/24/outline";
import { MissingDocumentRequest } from "./MissingDocumentRequest.ts";
import { MdrRejectionReason } from "./MdrRejectionReason.tsx";
import {
    createTable,
    RenderingPresetType,
    SearchState,
    SortingState,
    TableHeaderSpec
} from "../../../../../components/table/CreateTable";
import { MoneyLabel } from "../../../../../components/general/MoneyLabel.tsx";
import { InfoTooltip } from "../../../../../components/general/InfoTooltip.tsx";
import { formatBankAccountIdentifier } from "../../../../../lib/institutionDisplayNames.ts";
import { BankAccountLogo } from "../../../../../components/general/BankAccountLogo.tsx";

interface RowAttachDropzoneProps {
    transactionId: string;
    supportedFileTypes: Record<string, string[]>;
    supportedFileExtensions: string[];
    maxSize: number;
    onAttachFile: (transactionId: string, file: File) => Promise<void>;
    isAttaching: boolean;
}

function RowAttachDropzone({
    transactionId,
    supportedFileTypes,
    supportedFileExtensions,
    maxSize,
    onAttachFile,
    isAttaching
}: RowAttachDropzoneProps) {
    const [attachError, setAttachError] = useState<string | null>(null);

    const onDropAccepted = useCallback((acceptedFiles: File[]) => {
        setAttachError(null);
        if (acceptedFiles.length > 0) {
            void onAttachFile(transactionId, acceptedFiles[0]);
        }
    }, [transactionId, onAttachFile]);

    const onDropRejected = useCallback((fileRejections: FileRejection[]) => {
        const rejection = fileRejections[0];
        const code = rejection?.errors[0]?.code;
        if (code === 'file-invalid-type') {
            setAttachError(`Bestandstype niet ondersteund. Alleen ${supportedFileExtensions.join(', ')}.`);
        } else if (code === 'too-many-files') {
            setAttachError('Slechts één bestand per transactie.');
        } else {
            setAttachError(rejection?.errors[0]?.message ?? 'Bestand kon niet worden gekoppeld.');
        }
    }, [supportedFileExtensions]);

    const dropzone = useDropzone({
        onDropAccepted,
        onDropRejected,
        noDrag: true,
        disabled: isAttaching,
        maxFiles: 1,
        multiple: false,
        maxSize,
        accept: supportedFileTypes,
    });

    return (
        <div className="flex flex-col items-stretch">
            <div
                {...dropzone.getRootProps({
                    className: `flex items-center justify-center gap-2 px-3 py-2 border-2 border-dashed rounded-lg text-sm transition-colors ${isAttaching ? 'opacity-60 cursor-wait border-gray-300 bg-gray-50 text-gray-600' : 'border-gray-300 bg-gray-50 hover:bg-gray-100 text-gray-600 cursor-pointer'}`
                })}
                title="Sleep een document op de rij om te koppelen, of klik om te bladeren"
            >
                <input {...dropzone.getInputProps()} />
                <PaperClipIcon className="h-4 w-4" aria-hidden="true" />
                <span>{isAttaching ? "Bezig met koppelen…" : "Koppel document"}</span>
            </div>
            {attachError && (
                <span className="mt-1 text-xs text-red-500">{attachError}</span>
            )}
        </div>
    );
}

interface MissingDocumentsTableProps {
    data: MissingDocumentRequest[];
    onMarkAsPrivate?: (transactionId: string) => void;
    showPrivateAction?: boolean;
    fadingIds?: Set<string>;
    onAttachFile?: (transactionId: string, file: File) => Promise<void>;
    supportedFileTypes?: Record<string, string[]>;
    supportedFileExtensions?: string[];
    maxSize?: number;
    attachingIds?: Set<string>;
    onOpenRejectedDocument?: (documentId: string) => void;
    openableDocumentIds?: Set<string>;
}

export function MissingDocumentsTable({
    data,
    onMarkAsPrivate,
    showPrivateAction,
    fadingIds = new Set(),
    onAttachFile,
    supportedFileTypes,
    supportedFileExtensions,
    maxSize,
    attachingIds = new Set(),
    onOpenRejectedDocument,
    openableDocumentIds = new Set(),
}: MissingDocumentsTableProps) {
    const [sortingState, setSortingState] = useState<SortingState>({
        orderBy: 'booking_date',
        orderDescending: true
    });

    type Row = MissingDocumentRequest & { id: string };

    const rows: Row[] = data.map((doc) => ({
        ...doc,
        id: doc.transaction_id,
    }));

    if (sortingState.orderBy) {
        rows.sort((a, b) => {
            const getVal = (obj: any, path: string) => {
                return path.split('.').reduce((current, key) => current?.[key], obj);
            };

            const aValue = getVal(a, sortingState.orderBy);
            const bValue = getVal(b, sortingState.orderBy);

            if (aValue === bValue) return 0;
            if (aValue == null) return 1;
            if (bValue == null) return -1;

            const comparison = aValue > bValue ? 1 : -1;
            return sortingState.orderDescending ? -comparison : comparison;
        });
    }

    const canAttach = Boolean(onAttachFile && supportedFileTypes && supportedFileExtensions && maxSize);
    const isRowBusy = (transactionId: string) => fadingIds.has(transactionId) || attachingIds.has(transactionId);
    const openRejectedDocumentHandler = (documentId: string) => {
        if (!onOpenRejectedDocument || !openableDocumentIds.has(documentId)) return undefined;
        return () => onOpenRejectedDocument(documentId);
    };

    const tableSpec: TableHeaderSpec<Row> = {
        columns: [
            {
                name: '',
                field: 'id',
                style: { widthMultiplier: 0.2 },
                disableSorting: true,
                customRender: () => (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-6 h-6 text-gray-400">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m5.231 13.481L15 17.25m-4.5-15H5.625c-.621 0-1.125.504-1.125 1.125v16.5c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Zm3.75 11.625a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
                    </svg>
                )
            },
            {
                name: 'Omschrijving',
                field: 'relation_name',
                style: { widthMultiplier: 2 },
                customRender: (row: Row) => (
                    <div className="flex flex-col">
                        <span className="font-medium text-gray-800">{row.relation_name}</span>
                        <span className="text-xs text-gray-500">{row.desc}</span>
                        {row.rejection && (
                            <MdrRejectionReason
                                rejection={row.rejection}
                                onOpenDocument={openRejectedDocumentHandler(row.rejection.document_id)}
                            />
                        )}
                    </div>
                )
            },
            {
                name: 'Rekening',
                field: 'bank_account_iban',
                style: { widthMultiplier: 1.5 },
                disableSorting: true,
                customRender: (row: Row) => row.bank_account_iban ? (
                    <span className="inline-flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-full px-2 py-1 text-xs text-gray-600">
                        <BankAccountLogo
                            logoUrl={row.bank_logo_url}
                            className="w-4 h-4"
                        />
                        {formatBankAccountIdentifier(row.bank_account_iban, row.institution_name)}
                    </span>
                ) : null
            },
            {
                name: 'Bedrag',
                field: 'total_amount_payable.signed_amount',
                customRender: (row: Row) => <MoneyLabel value={Number(row.total_amount_payable.signed_amount) / 100} colorAid={ true} />,
                style: { widthMultiplier: 0.8 }
            },
            {
                name: 'Datum',
                field: 'booking_date',
                renderingPreset: RenderingPresetType.Date,
                style: { widthMultiplier: 1, nowrap: true }
            },
            ...(showPrivateAction ? [{
                name: <span className="flex items-center gap-1">Acties <InfoTooltip text="Markeer een transactie als privé. De transactie verdwijnt uit de lijst en er hoeft geen document geüpload te worden. Dit kan later alleen door de boekhouder worden gewijzigd." /></span>,
                field: 'private_action',
                disableSorting: true,
                style: {
                    nowrap: true,
                    widthMultiplier: 0.75
                },
                customRender: (row: Row) => fadingIds.has(row.id) ? (
                    <span className="flex items-center gap-1 text-sm text-green-600 font-medium px-3 py-1">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                            <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" />
                        </svg>
                    </span>
                ) : (
                    <button
                        className="text-sm text-gray-700 hover:text-gray-900 font-medium whitespace-nowrap flex items-center gap-1 bg-gray-100 hover:bg-gray-200 rounded-full px-3 py-1 transition-colors"
                        onClick={(e) => {
                            e.stopPropagation();
                            onMarkAsPrivate?.(row.id);
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                            <path d="M10 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM3.465 14.493a1.23 1.23 0 0 0 .41 1.412A9.957 9.957 0 0 0 10 18c2.31 0 4.438-.784 6.131-2.1.43-.333.604-.903.408-1.41a7.002 7.002 0 0 0-13.074.003Z" />
                        </svg>
                        Privé
                    </button>
                )
            }] : []),
            ...(canAttach ? [{
                name: 'Koppelen',
                field: 'attach_action',
                disableSorting: true,
                style: { widthMultiplier: 1.5 },
                customRender: (row: Row) => (
                    <RowAttachDropzone
                        transactionId={row.id}
                        supportedFileTypes={supportedFileTypes!}
                        supportedFileExtensions={supportedFileExtensions!}
                        maxSize={maxSize!}
                        onAttachFile={onAttachFile!}
                        isAttaching={isRowBusy(row.id)}
                    />
                )
            }] : []),
        ],
        rowDropzone: canAttach ? {
            onDropFile: (row: Row, file: File) => { void onAttachFile!(row.id, file); },
            hint: "Drop hier om te koppelen",
            disabled: (row: Row) => isRowBusy(row.id),
        } : undefined,
        onEmptyRender: () => <span className="text-gray-500">Geen documenten.</span>,
        sorting: {
            sortingState: [sortingState, setSortingState],
            inMemory: false
        },
        search: {
            searchFn: (row: Row, searchState: SearchState) => {
                const query = searchState.currentQuery.toLowerCase();
                return (row.relation_name?.toLowerCase().includes(query) ?? false)
                    || (row.desc?.toLowerCase().includes(query) ?? false)
                    || (row.bank_account_iban?.toLowerCase().includes(query) ?? false);
            }
        },
        onQueryUpdate: () => {},
        options: {
            disableTopPaginationWidget: true,
            disableBottomPaginationWidget: true,
            hideTableHeader: false
        }
    };

    const TableComponent = createTable<Row>(rows, tableSpec);

    return (
        <>
            <style>{`
                @keyframes fadeCollapse {
                    0% { opacity: 1; max-height: 60px; }
                    50% { opacity: 0; max-height: 60px; }
                    100% { opacity: 0; max-height: 0; overflow: hidden; }
                }
                ${[...fadingIds].map(id =>
                    `tr[data-item-id="${id}"] { animation: fadeCollapse 0.4s ease-out forwards; }`
                ).join('\n')}
            `}</style>
            {TableComponent()}
        </>
    );
}
